import { useState, useEffect, useCallback } from 'react';
import {
  getGoals, saveGoal,
  getAdminLeadsStats, getAdminFunnelPageStats, getAdminWistiaStats, getAdminRySalesStats,
} from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

const FONT = "'DM Sans', system-ui, sans-serif";

const C = {
  bg:       '#FFF8F7',
  text:     '#3D1F02',
  heading:  '#6B3E0A',
  accent:   '#AD795B',
  gold:     '#C9A84C',
  border:   '#E8D5C4',
  card:     '#ffffff',
  green:    '#2E7D5E',
  greenBg:  '#F0FAF5',
  amber:    '#B45309',
  amberBg:  '#FFFBEB',
  red:      '#B91C1C',
  redBg:    '#FEF2F2',
  track:    '#F3E9DF',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// The earliest date this tab will measure from — the launch floor, the same
// date backend/src/config/launch.js clamps every funnel query to. Before it
// there is no landing tracking to measure against.
const PERIOD_MIN = '2026-08-14';
const todayISO = () => new Date().toISOString().slice(0, 10);

// ── THE SIX WINDOWS ─────────────────────────────────────────────────────────
// The toggle is the tab's ONLY time control. Whatever it is set to is the
// window the typed targets apply to, and every stored window is one of these
// same six, so the two can never disagree about what "3 months" is. The
// backend holds the identical list as a CHECK constraint on period_days.
// `days` is NOMINAL — a month is
// 30 days here, a quarter 90 — because these numbers are used for the SCALING
// RATIO, and nominal months make Ben's own example come out exactly: 300 over
// 3 months is 100 at one month and 600 at six. Real months (30.44 days) would
// give 98.9 and 599.
//
// ⚠ NOMINAL DAYS ARE NOT USED FOR WINDOW BOUNDARIES. Those are computed by
// anchored calendar arithmetic below — one month after 7 Sept is 7 Oct, not
// 7 Oct less half a day. Two different jobs, two different arithmetics, and
// conflating them is how a "month" quietly becomes 30 days on a 31-day month.
const WINDOWS = [
  { id: '24h', label: '24 hours', days: 1,   months: 0  },
  { id: '1w',  label: '1 week',   days: 7,   months: 0  },
  { id: '1m',  label: '1 month',  days: 30,  months: 1  },
  { id: '3m',  label: '3 months', days: 90,  months: 3  },
  { id: '6m',  label: '6 months', days: 180, months: 6  },
  { id: '12m', label: '12 months',days: 365, months: 12 },
];
// ── WHICH METRICS ARE RATES — THE ONLY PLACE THIS IS RECORDED ───────────────
// A rate is a percentage OF something, not a quantity accumulated over time, so
// it does not scale with the window: 20% a month is 20% a quarter, not 60%.
// A count does scale: 20 a month is 60 a quarter.
//
// ⚠ A NEW RATE METRIC MUST BE ADDED HERE, and nowhere else. Anything missing
// from this set is treated as a count and MULTIPLIED by the window, so a 20%
// opt-in-rate target would be shown as a 60% target at three months. The `unit`
// the row renders with is derived from this set for exactly that reason —
// there is no second list to forget to update.
const RATE_METRIC_KEYS = new Set(['optin_rate']);

const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
// Calendar months from an anchor, clamping the day-of-month so 31 Jan + 1
// month is 28/29 Feb rather than spilling into March.
const addMonths = (iso, n) => {
  const d = new Date(iso + 'T00:00:00Z');
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
};
const daysBetween = (a, b) =>
  Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);

// ── THE CURRENT WINDOW, ANCHORED TO THE PERIOD START ────────────────────────
// Windows are consecutive spans of the chosen length laid down from the period
// start — NOT calendar weeks or calendar months. If the period starts on the
// 7th, week three begins on the 21st whatever weekday that is, and month two
// begins on the 7th of the next month. `i` is simply how many whole windows
// have elapsed since the anchor.
//
// Before the period begins, i is 0: the first window, not yet started. That is
// Ben's live case — his period start is tomorrow — and it must read as "not
// begun", never as a negative index into the past.
function currentWindow(anchorISO, win, today) {
  if (!anchorISO) return null;
  let i = 0;
  if (today > anchorISO) {
    if (win.months > 0) {
      while (addMonths(anchorISO, (i + 1) * win.months) <= today) i++;
    } else {
      i = Math.floor(daysBetween(anchorISO, today) / win.days);
    }
  }
  const start = win.months > 0 ? addMonths(anchorISO, i * win.months) : addDays(anchorISO, i * win.days);
  const nextStart = win.months > 0 ? addMonths(anchorISO, (i + 1) * win.months) : addDays(anchorISO, (i + 1) * win.days);
  return { index: i, start, end: addDays(nextStart, -1), nextStart, notStarted: today < anchorISO };
}

// "2 months, 3 weeks, 2 days in" / "2 weeks and 5 days in" — Ben's own phrasing.
// Months are counted as calendar months from the window start so the figure
// matches the boundaries above; the remainder becomes whole weeks then days.
function elapsedInWindow(w, today) {
  if (!w || w.notStarted) return 'not started yet';
  let months = 0;
  while (addMonths(w.start, months + 1) <= today) months++;
  const afterMonths = addMonths(w.start, months);
  const restDays = daysBetween(afterMonths, today);
  const weeks = Math.floor(restDays / 7);
  const days = restDays % 7;
  const parts = [];
  if (months) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (weeks)  parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
  if (days || !parts.length) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  const joined = parts.length > 1
    ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1]
    : parts[0];
  return `${joined} in`;
}
const fmtDay = (iso) =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
// Whole days elapsed since the period began, inclusive of today.
const daysSince = (iso) => {
  const d = Math.floor((Date.parse(todayISO() + 'T00:00:00Z') - Date.parse(iso + 'T00:00:00Z')) / 86400000) + 1;
  return d <= 1 ? 'day 1' : `day ${d}`;
};

function pct(actual, target) {
  if (!target || target === 0) return 0;
  return Math.min(100, Math.round((actual / target) * 100));
}

function statusColor(p) {
  if (p >= 100) return { bar: C.green,  bg: C.greenBg, text: C.green  };
  if (p >= 60)  return { bar: C.gold,   bg: C.amberBg, text: C.amber  };
  return              { bar: C.red,    bg: C.redBg,   text: C.red    };
}

function statusLabel(p) {
  if (p >= 100) return '✓ Hit';
  if (p >= 80)  return '↑ On track';
  if (p >= 50)  return '⚠ Behind';
  return '✕ Off track';
}

// ── Inline editable number ────────────────────────────────────────────────────
function EditableNumber({ value, onSave, color = C.text, size = 32, bold = true, display }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => { setDraft(String(value ?? 0)); setEditing(true); };
  const commit = () => {
    const n = parseInt(draft, 10);
    if (!isNaN(n) && n !== value) onSave(n);
    setEditing(false);
  };

  // View-only admins see the figure but cannot edit it (no click-to-edit).
  if (user?.role !== 'owner') {
    return (
      <span style={{ fontSize: size, fontWeight: bold ? 700 : 400, color, fontFamily: FONT, lineHeight: 1.1 }}>
        {value ?? 0}
      </span>
    );
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{
          width: 80, fontSize: size, fontWeight: bold ? 700 : 400, color,
          border: `2px solid ${C.gold}`, borderRadius: 6, padding: '2px 6px',
          fontFamily: FONT, background: '#fffdf5', outline: 'none',
        }}
      />
    );
  }

  return (
    <span
      onClick={start}
      title="Click to edit"
      style={{
        fontSize: size, fontWeight: bold ? 700 : 400, color,
        cursor: 'pointer', borderBottom: `2px dashed ${C.border}`,
        fontFamily: FONT, lineHeight: 1.1,
      }}
    >
      {display ?? value ?? 0}
    </span>
  );
}

// ── Single metric row ────────────────────────────────────────────────────────
function MetricRow({ goal, onUpdate, bottleneck, saveError, onSaveError, windowDays }) {
  // THREE STATES THIS ROW NOW HAS TO SAY OUT LOUD, because each is a different
  // kind of "no number" and collapsing them into 0% would be a lie:
  //   unavailable — the metric cannot be produced on the active basis at all
  //   no target   — a real actual with nothing to measure it against yet
  //   unit '%'    — the actual IS a percentage, so "17 / 25" needs a % on both
  const unavailable = goal.unavailable === true;
  const hasTarget   = goal.target_value !== null && goal.target_value !== undefined && goal.target_value !== '';
  const isPct       = goal.unit === '%';
  const p  = unavailable || !hasTarget ? 0 : pct(goal.actual, goal.target_value);
  const sc = statusColor(p);
  const gap = hasTarget && !unavailable ? Math.max(0, goal.target_value - goal.actual) : 0;
  // One decimal ALWAYS on a rate, so the two views cannot read '17%' and
  // '12.2%' side by side and look like different kinds of number.
  const fmt = (v) => (isPct ? `${Number(v).toFixed(1)}%` : v);

  // ── A FAILED SAVE MUST NOT LOOK LIKE A SAVE ─────────────────────────────
  // Both of these used to be `.catch(() => {})` after an optimistic update:
  // the new number went on screen and the error was thrown away. That is how
  // five targets were lost without anyone noticing — the tab showed exactly
  // what Ben had typed until he reloaded.
  //
  // Now the optimistic update still happens (the field must feel responsive),
  // but a rejection PUTS THE OLD VALUE BACK and raises a message on the row.
  // Reverting matters as much as the message: leaving the typed number visible
  // beside "couldn't save" would still show a figure that does not exist.
  //
  // metric_label goes with it so the server can insert a row for a metric that
  // has none — the column is NOT NULL with no default.
  const handleTargetSave = async (val) => {
    // ── STORED EXACTLY AS TYPED, WITH THE WINDOW IT WAS TYPED AT ──────────
    // No conversion on the way in. The number Ben typed is the number stored,
    // and the toggle setting he typed it at is stored beside it, so the view he
    // typed in always shows his own figure back — 61 at 3 months is 61 at 3
    // months forever. Converting on save is what made that impossible: 61 would
    // become 20 a month (target_value is an integer, so the third is lost) and
    // read back as 60.
    //
    // Only the windows he did NOT type at are approximations, which is correct
    // and unavoidable.
    const typed = Number(val);
    // Revert needs what was STORED, not what was on screen — the displayed
    // figure is converted, so putting it back would write a converted number
    // into the raw slot and multiply the target by the window ratio.
    const prevValue = goal.stored_value;
    const prevDays  = goal.stored_days;
    onSaveError(goal.metric_key, null);
    onUpdate(goal.metric_key, { target_value: typed, period_days: windowDays });
    try {
      await saveGoal({
        metric_key: goal.metric_key, metric_label: goal.metric_label,
        target_value: typed, period_days: windowDays,
      });
    } catch (e) {
      onUpdate(goal.metric_key, { target_value: prevValue, period_days: prevDays });
      onSaveError(goal.metric_key, 'Target not saved — check your connection and try again.');
    }
  };

  const handleActualSave = async (val) => {
    const previous = goal.actual;
    onSaveError(goal.metric_key, null);
    onUpdate(goal.metric_key, { manual_actual: val, actual: val });
    try {
      await saveGoal({ metric_key: goal.metric_key, metric_label: goal.metric_label, manual_actual: val });
    } catch (e) {
      onUpdate(goal.metric_key, { manual_actual: previous, actual: previous });
      onSaveError(goal.metric_key, 'Not saved — check your connection and try again.');
    }
  };

  return (
    <div style={{
      background: bottleneck ? sc.bg : C.card,
      border: `1px solid ${bottleneck ? sc.bar : C.border}`,
      borderLeft: `4px solid ${sc.bar}`,
      borderRadius: 12,
      padding: '18px 20px',
      marginBottom: 10,
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.heading, fontFamily: FONT }}>
            {goal.metric_label}
          </span>
          {bottleneck && (
            <span style={{ fontSize: 11, fontWeight: 700, background: sc.bar, color: '#fff', borderRadius: 20, padding: '2px 8px', fontFamily: FONT }}>
              BOTTLENECK
            </span>
          )}
          {!goal.is_auto && !unavailable && (
            <span style={{ fontSize: 11, color: C.accent, fontFamily: FONT }}>✎ manual</span>
          )}
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: unavailable || !hasTarget ? C.accent : sc.text, fontFamily: FONT }}>
          {unavailable ? 'all traffic only' : !hasTarget ? 'no target yet' : statusLabel(p)}
        </span>
      </div>

      {/* Numbers row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
        {/* Actual */}
        <div>
          <div style={{ fontSize: 11, color: C.accent, fontFamily: FONT, marginBottom: 2 }}>ACTUAL</div>
          {unavailable ? (
            <span style={{ fontSize: 32, fontWeight: 700, color: C.accent, fontFamily: FONT, lineHeight: 1.1 }}>—</span>
          ) : goal.is_auto ? (
            <span style={{ fontSize: 32, fontWeight: 700, color: C.text, fontFamily: FONT, lineHeight: 1.1 }}>
              {fmt(goal.actual)}
            </span>
          ) : (
            <EditableNumber value={goal.actual} onSave={handleActualSave} color={C.text} />
          )}
        </div>

        {/* Divider */}
        <div style={{ fontSize: 24, color: C.border, paddingBottom: 4 }}>/</div>

        {/* Target */}
        <div>
          <div style={{ fontSize: 11, color: C.accent, fontFamily: FONT, marginBottom: 2 }}>TARGET</div>
          {/* Still editable when unset — clicking the dash is how Ben sets the
              first one, and POST /goals now inserts a row when none exists. */}
          <EditableNumber
            value={hasTarget ? goal.target_value : 0}
            display={hasTarget ? undefined : '—'}
            onSave={handleTargetSave}
            color={hasTarget ? C.heading : C.accent} />
        </div>

        {/* % and gap */}
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: unavailable || !hasTarget ? C.border : sc.text, fontFamily: FONT, lineHeight: 1 }}>
            {unavailable || !hasTarget ? '—' : `${p}%`}
          </div>
          {gap > 0 && (
            <div style={{ fontSize: 12, color: C.accent, fontFamily: FONT, marginTop: 2 }}>
              {fmt(gap)} to go
            </div>
          )}
          {unavailable && (
            <div style={{ fontSize: 11, color: C.accent, fontFamily: FONT, marginTop: 2, maxWidth: 190 }}>
              no paid-traffic version exists
            </div>
          )}
        </div>
      </div>

      {saveError && (
        <div style={{
          marginBottom: 10, padding: '7px 10px', borderRadius: 8,
          background: C.redBg, border: `1px solid ${C.red}`,
          fontSize: 12, fontWeight: 600, color: C.red, fontFamily: FONT,
        }}>
          ⚠ {saveError}
        </div>
      )}

      {/* Progress bar */}
      <div style={{ height: 8, background: C.track, borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${p}%`,
          background: `linear-gradient(90deg, ${sc.bar}cc, ${sc.bar})`,
          borderRadius: 99,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export const AdminGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals]           = useState([]);
  const [periodLabel, setPeriodLabel] = useState('30 Days — Paid Traffic');
  const [periodStart, setPeriodStart] = useState('');
  const [windowInfo, setWindowInfo]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft]   = useState('');

  // ── THE VIEW TOGGLE ────────────────────────────────────────────────────────
  // Ben, 2026-09-05: two views, defaulting to Ad Metrics. 'ads' sends
  // paidOnly=1 to exactly the endpoints Ad Metrics itself sends it to; 'all'
  // sends nothing, which is what Pages & Masterclass does. That is the whole
  // mechanism — there is no second definition of "paid" on this tab.
  const [view, setView] = useState('ads');
  // B6: same shape as the view toggle — component state, so it persists while
  // the tab is open and resets on remount exactly as that one does.
  const [windowId, setWindowId] = useState('3m');
  // metric_key -> message, set only when a save actually failed.
  const [saveErrors, setSaveErrors] = useState({});
  const setSaveError = (key, msg) =>
    setSaveErrors(prev => (msg ? { ...prev, [key]: msg } : (({ [key]: _drop, ...rest }) => rest)(prev)));
  const [live, setLive] = useState(null);

  // EVERY FIGURE COMES FROM THE ENDPOINT THAT ALREADY OWNS IT. These are the
  // same four calls Pages & Masterclass and Ad Metrics make, with the same
  // paidOnly flag, so a number here cannot drift from the number there — it is
  // literally the same response field. That is why this tab computes nothing of
  // its own except the opt-in rate, which is a division of two of them.
  //
  // The delivery half (course completed, BMS booked/attended) comes from
  // /goals, because it is the only place those exist with gifts and comps
  // taken out — /funnel-full counts them as a journey view and applies no
  // exclusion at all to the BMS rows.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const paidOnly = view === 'ads';
      // ── ONE WINDOW FOR EVERY METRIC ────────────────────────────────────────
      // /goals is fetched FIRST because it owns the period start, and the four
      // stats calls then take it as their `from`. Before this, only the three
      // delivery figures honoured the period while everything above them counted
      // from 14 Aug — so this tab compared one period's course completions
      // against three weeks of accumulated opt-ins.
      //
      // NO NEW MECHANISM. These four endpoints have always accepted (from, to);
      // it is the same argument every other tab's timeframe control passes, and
      // each one runs it through floorFrom(), which clamps anything earlier than
      // 14 Aug 2026 up to that date. The hard minimum is therefore enforced by
      // the code that already enforces it everywhere else — nothing here
      // re-implements it.
      // /goals first: it owns the period start AND its length, and the window
      // cannot be computed without both.
      const gRes = await getGoals(paidOnly);
      const data = gRes.data;
      const win = WINDOWS.find(w => w.id === windowId) || WINDOWS[3];
      const cw = currentWindow(data.periodStart || null, win, todayISO());
      // EVERY endpoint gets the SAME window, /goals included — so the delivery
      // figures and the top-of-funnel ones can never be describing different
      // spans. `to` is sent as well as `from`: B2 says a window's actual is
      // that window's own count, not a running total.
      const from = cw ? cw.start : (data.periodStart || undefined);
      const to   = cw ? cw.end   : undefined;
      const [gWin, leads, fps, wistia, rySales] = await Promise.all([
        getGoals(paidOnly, from, to),
        getAdminLeadsStats(from, to, paidOnly),
        getAdminFunnelPageStats(from, to, paidOnly),
        getAdminWistiaStats(from, to, paidOnly),
        getAdminRySalesStats(from, to, paidOnly),
      ]);
      setWindowInfo(cw);
      setGoals(data.goals || []);
      // autoActuals comes from the WINDOWED /goals call; the targets, period
      // start and length come from the unwindowed one.
      setLive({ auto: gWin.data.autoActuals || {}, trace: gWin.data.paidTrace || null,
                leads: leads.data, fps: fps.data, wistia: wistia.data, rySales: rySales.data });
      if (data.goals?.length) {
        setPeriodLabel(data.goals[0].period_label || '30 Days — Paid Traffic');
        setPeriodStart(data.periodStart || '');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [view, windowId]);

  useEffect(() => { load(); }, [load]);

  // Optimistic local update. Adds the row when there isn't one yet — the five
  // new metrics have no ry_goals row until Ben sets their first target, and
  // without this the number he just typed would vanish until the next reload
  // even though the server saved it.
  const handleUpdate = (key, patch) => {
    setGoals(prev => prev.some(g => g.metric_key === key)
      ? prev.map(g => g.metric_key === key ? { ...g, ...patch } : g)
      : [...prev, { metric_key: key, ...patch }]);
  };

  // Same discipline as the target editors (2b5da9f): a rejected save must be
  // seen, not logged. The backend refuses a period start before 14 Aug 2026 or
  // after today, and its message is what gets shown — the rule lives there, so
  // the wording should come from there too rather than being guessed at twice.
  const [periodError, setPeriodError] = useState(null);
  const savePeriod = async () => {
    setSavingPeriod(true);
    setPeriodError(null);
    try {
      await Promise.all(
        goals.map(g => saveGoal({
          metric_key:   g.metric_key,
          metric_label: g.metric_label,
          period_label: periodLabel,
          period_start: periodStart || null,
          // NOT period_days. The window a target belongs to is recorded on its
          // own row when Ben types it, at whatever the toggle was set to. One
          // global length here is the second time control that was removed.
        }))
      );
      // Re-read every metric against the new window; nothing stale is left on
      // screen because `load()` replaces the whole payload, not part of it.
      await load();
    } catch (e) {
      setPeriodError(
        e?.response?.data?.error || 'Period not saved — check your connection and try again.'
      );
    }
    finally { setSavingPeriod(false); }
  };

  // ── BEN'S TEN, IN FUNNEL ORDER ─────────────────────────────────────────────
  // Built here, not on the server, and deliberately: every value is lifted
  // straight out of the response that already owns it, so "reconciles with the
  // tab it came from" is true by construction rather than by coincidence.
  //
  // `key` maps to a ry_goals row where one exists, so the six existing targets
  // carry across untouched. The five without a row show their real actual and
  // say "no target yet" — none is invented here.
  //
  // `unavailable` marks the four that have no paid-traffic version. They come
  // from /funnel-full, which takes no paidOnly flag, so on the Ad Metrics view
  // they render a dash and say why rather than quietly showing all traffic.
  const paidView = view === 'ads';
  const L = live || {};
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const optinVisitors = num(L.fps?.optin?.uniqueVisitors);
  const optinCount    = num(L.leads?.totalLeads);
  const optinRate     = optinVisitors > 0 ? Math.round((optinCount / optinVisitors) * 1000) / 10 : 0;
  // Paid/not-paid/untraceable split from /goals; null on the all-traffic view.
  const trace = L.trace || null;

  const rowFor = (key) => goals.find(g => g.metric_key === key) || {};

  // ── ONE TIME CONTROL: THE TOGGLE ────────────────────────────────────────
  // Each target is stored exactly as typed, with the window it was typed at on
  // its OWN row (period_days). Display converts; storage never does.
  //
  //   shown = round(stored x selectedWindowDays / thatRowsStoredDays)
  //
  // Because nothing is rewritten on save, the window a figure was typed in
  // always returns that exact figure — 61 at 3 months is 61 at 3 months, not
  // 60. Rounding happens ONCE, on the final displayed figure, so the six
  // windows cannot drift apart the way repeated per-step rounding would.
  //
  // Days are nominal (a month is 30, a quarter 90), so 300 over 3 months is
  // exactly 100 at one month and 600 at six. Real months (30.44 days) would
  // give 98.9 and 599.
  //
  // ⚠ A RATE DOES NOT SCALE — the set is RATE_METRIC_KEYS, at the top of this
  // file, and that is the only place the fact is recorded.
  const selWin = WINDOWS.find(w => w.id === windowId) || WINDOWS[3];
  const scaleTarget = (stored, isRate, storedDays) => {
    if (stored === null || stored === undefined || stored === '') return null;
    if (isRate) return Number(stored);
    // No window recorded yet (a metric that has never had a target saved):
    // show it as it stands rather than inventing a ratio for it.
    if (!storedDays) return Number(stored);
    return Math.round((Number(stored) * selWin.days) / storedDays);
  };
  const M = (key, label, actual, opts = {}) => {
    const row = rowFor(key);
    const manual = opts.manual === true;
    // Derived, never passed in — see RATE_METRIC_KEYS.
    const isRate = RATE_METRIC_KEYS.has(key);
    return {
      metric_key: key,
      metric_label: label,
      // A manual metric keeps whatever Ben typed; an auto one shows the live
      // figure and ignores any stale manual value left on the row.
      actual: manual ? num(row.manual_actual) : actual,
      target_value: scaleTarget(row.target_value ?? null, isRate, row.period_days ?? null),
      // The raw row carried alongside the converted projection, so a failed
      // save can put back what was actually stored rather than what happened to
      // be on screen at the time.
      stored_value: row.target_value ?? null,
      stored_days:  row.period_days ?? null,
      is_auto: !manual,
      unit: isRate ? '%' : opts.unit,
      unavailable: opts.allTrafficOnly === true && paidView,
    };
  };

  const metrics = live ? [
    M('optin_rate',       'Opt-in rate',              optinRate),
    M('opt_ins',          'Opt-ins',                  optinCount),
    // ── CONSUMPTION MEANS 25%+ (Ben, 2026-09-06) ──────────────────────────
    // It used to read leads.masterclass_watched, which is NOT a 25% figure and
    // is not even one threshold: routes/leads.js POST /watched has been fired
    // at 25% since 2026-08-27 and at 80% before that, so the column is a mix of
    // two meanings depending on when the person watched.
    //
    // Repointed to the reached_25 milestone that already exists, is already on
    // this tab's /wistia-stats response, and already honours paidOnly. It
    // reconciles exactly with the Funnel Overview's "Watched masterclass
    // (25%+)" row — both read 44 on all traffic, measured 2026-09-06 08:27Z —
    // so no third definition of "watched" enters the system.
    //
    // GRAIN CHANGES WITH IT, and that is the honest reading: the old figure
    // counted LEADS who watched, the new one counts PEOPLE who reached 25%,
    // including anyone who reached the masterclass without opting in — the link
    // works without an opt-in. Same basis as the masterclass card.
    M('masterclass_views','Masterclass consumption (25%+)', num(L.wistia?.masterclass?.milestones?.p25?.count)),
    M('mc_cta_clicks',    'Masterclass CTA clicks',   num(L.wistia?.masterclass?.cta?.clicked?.count)),
    // SALES PAGE goals: this goal window, like every other goal (the sales
    // video switch is gone).
    M('sales_page_views', 'Sales page landings',      num(L.fps?.uniqueViewers)),
    M('sales_cta_clicks', 'Sales page CTA clicks',    num(L.fps?.checkout?.ctaClicksRaw)),
    M('purchases',        'RY purchases',             num(L.rySales?.paidPurchasers)),
    // NARROWED TO PAID TRAFFIC AS OF 2026-09-06, so no dash any more. /goals
    // now takes paidOnly and resolves each enrolment to its original source
    // through BOTH bridges — the visitor_id the purchase carried, and the
    // opt-in device recovered by email through leads + visitor_links. Someone
    // who opts in on a phone and buys on a laptop is still attributed.
    M('course_completed', 'Course completed',         num(L.auto?.course_completed)),
    M('calls_booked',     'BMS booked',               num(L.auto?.calls_booked)),
    M('calls_attended',   'BMS attended',             num(L.auto?.calls_attended)),
    M('clients_signed',   '1:1 upsells',              0, { manual: true }),
  ] : [];

  // Find the bottleneck — the metric with the lowest % of target
  // (only among metrics with a target > 0 and actual < target)
  const bottleneckKey = metrics.reduce((worst, g) => {
    if (!g.target_value) return worst;
    const p = pct(g.actual, g.target_value);
    if (!worst) return p < 100 ? g.metric_key : null;
    const worstPct = pct(
      metrics.find(x => x.metric_key === worst)?.actual ?? 0,
      metrics.find(x => x.metric_key === worst)?.target_value ?? 1
    );
    return p < worstPct ? g.metric_key : worst;
  }, null);

  // Overall progress: average across the metrics that can actually be scored —
  // one with no target, or none on this basis, has no percentage to average and
  // counting it as 0% would drag the headline down for a reason that is about
  // configuration rather than performance.
  const scorable = metrics.filter(g => !g.unavailable && g.target_value);
  const overallPct = scorable.length
    ? Math.round(scorable.reduce((sum, g) => sum + pct(g.actual, g.target_value), 0) / scorable.length)
    : 0;

  if (loading) {
    return (
      <div style={{ fontFamily: FONT, color: C.heading, padding: 40 }}>Loading goals…</div>
    );
  }

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: C.heading }}>
          Goals & Targets
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: C.accent }}>
          Set your targets, track actuals. Auto metrics update live — manual ones you enter yourself.
          Click any number to edit it.
        </p>
        {/* ── WHY THIS TAB DISAGREES WITH THE OTHERS ────────────────────────
            Without this line the disagreement is invisible and unexplained:
            RY Purchases reads 1 here and the Funnel Overview's "Purchased"
            row reads 7, from the same table on the same day. In three months
            that looks like a bug rather than a decision.

            Ben's decision, 2026-09-05: a target measures real paying
            customers, so gifted and comped enrolments come out — here, and
            only here. Every other surface goes on counting gifts on purpose
            (config/countedUsers.js explains why a gift redeemer IS a funnel
            conversion), so the two will keep differing and that is correct.

            Deliberately says WHICH metrics move. Opt-ins and consumption are
            not affected: being gifted or comped later does not un-opt-in
            someone, so those still count everyone but Ben's own accounts,
            exactly as the Funnel Overview and Contacts always have. */}
      </div>

      {periodError && (
        <div style={{
          margin: '0 0 12px', padding: '9px 12px', borderRadius: 8,
          background: C.redBg, border: `1px solid ${C.red}`,
          fontSize: 12, fontWeight: 600, color: C.red, fontFamily: FONT,
        }}>
          ⚠ {periodError}
        </div>
      )}

      {/* ── VIEW TOGGLE ────────────────────────────────────────────────────
          Two populations, one tab. Same button styling as the timeframe rows
          on Pages & Masterclass and Traffic & UTM — this is not a new visual
          idea, it is the one the dashboard already uses for "pick a basis". */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {[['ads', 'Ad Metrics'], ['all', 'Pages & Masterclass']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              border: `1px solid ${view === id ? C.heading : C.border}`,
              background: view === id ? C.heading : C.card,
              color: view === id ? '#FFF8F7' : C.accent, cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── TIME WINDOW TOGGLE ─────────────────────────────────────────────
          Same styling as the view toggle above and the timeframe rows on the
          other tabs — one visual idea for "pick a basis", not a new one. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {WINDOWS.map(w => (
          <button
            key={w.id}
            onClick={() => setWindowId(w.id)}
            style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
              border: `1px solid ${windowId === w.id ? C.heading : C.border}`,
              background: windowId === w.id ? C.heading : C.card,
              color: windowId === w.id ? '#FFF8F7' : C.accent, cursor: 'pointer',
            }}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* ── B4: WHERE HE IS IN THE CURRENT WINDOW ──────────────────────────
          The elapsed time and the window's own dates, and NOTHING that judges
          them. 18 of 66 on day three of seven is not behind, and a pace
          warning here would say it is. Ben reads the two numbers himself. */}
      {windowInfo && (
        <div style={{ fontSize: 12, color: C.accent, fontFamily: FONT, marginBottom: 14, lineHeight: 1.6 }}>
          <strong style={{ color: C.text }}>{selWin.label}</strong>{' '}
          {windowInfo.notStarted
            ? <>window begins {fmtDay(windowInfo.start)} — not started yet.</>
            : <>window {windowInfo.index + 1}: {fmtDay(windowInfo.start)} → {fmtDay(windowInfo.end)}, {elapsedInWindow(windowInfo, todayISO())}.</>}
          {' '}Targets are for this window; change the window and they convert.
        </div>
      )}

      {/* ── WHAT THIS TAB IS COUNTING, SAID OUT LOUD ───────────────────────
          Three facts that each make a figure here differ from somewhere else,
          and none of them is guessable from the numbers:
            which population is active (the toggle above),
            that gifts and comps are out (Ben's decision, 2026-09-05),
            that everything is floored at 14 Aug like every other tab — which
            is the fix for "Masterclass Opt-ins 127", a figure that counted
            every lead ever recorded because this tab alone had no floor. */}
      <p style={{ margin: '0 0 18px', fontSize: 12, color: C.accent, fontStyle: 'italic', lineHeight: 1.6 }}>
        {view === 'ads'
          ? <>Showing <strong style={{ color: C.text, fontStyle: 'normal' }}>paid traffic</strong> — the same population as the Ad Metrics tab. </>
          : <>Showing <strong style={{ color: C.text, fontStyle: 'normal' }}>all traffic</strong> — the same population as Pages &amp; Masterclass. </>}
        Counts real paying customers: gifted and comped enrolments and your own admin accounts are
        left out.{' '}
        {/* ── A6: THE ACTIVE WINDOW, SAID OUT LOUD ────────────────────────
            Every figure on the tab now measures from this one date, so it has
            to be on screen — a reader who cannot see the window cannot judge
            the number. The future case is called out rather than left to look
            like a broken tab: Ben's stored start is 2026-09-07, which has not
            arrived, and every metric legitimately reads zero until it does. */}
        {periodStart ? (
          periodStart > todayISO() ? (
            <strong style={{ color: C.text, fontStyle: 'normal' }}>
              This period starts on {fmtDay(periodStart)} — it has not begun, so every figure below
              reads zero until then.
            </strong>
          ) : (
            <>Measuring from <strong style={{ color: C.text, fontStyle: 'normal' }}>{fmtDay(periodStart)}</strong>{' '}
            ({daysSince(periodStart)}), the period start set above.</>
          )
        ) : (
          <>Measuring from <strong style={{ color: C.text, fontStyle: 'normal' }}>14 Aug 2026</strong>, the
          earliest date with reliable tracking — set a period start above to measure from a later one.</>
        )}
        {view === 'ads' && trace && (
          <>
            {' '}Course, BMS and enrolment figures are narrowed to people whose first opt-in landing
            carried an ad tag — matched on the visitor id their purchase carried, or on the device
            they opted in on, found by email.
            {trace.untraceable > 0 && (
              <> <strong style={{ color: C.text, fontStyle: 'normal' }}>
                {trace.untraceable} of {trace.countable}
              </strong>{' '}
              {trace.untraceable === 1 ? 'has' : 'have'} no opt-in landing at all, so
              {trace.untraceable === 1 ? ' it' : ' they'} cannot be called paid or not —
              {trace.untraceable === 1 ? ' it is' : ' they are'} left out of these figures rather
              than counted as non-paid.</>
            )}
          </>
        )}
      </p>

      {/* ── Period config bar (owner-only: edit period + save) ── */}
      {user?.role === 'owner' && (
      <div style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 11, color: C.accent, marginBottom: 4 }}>PERIOD NAME</div>
          {editingLabel ? (
            <input
              autoFocus
              value={labelDraft}
              onChange={e => setLabelDraft(e.target.value)}
              onBlur={() => { setPeriodLabel(labelDraft); setEditingLabel(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { setPeriodLabel(labelDraft); setEditingLabel(false); } }}
              style={{ fontSize: 14, fontWeight: 700, color: C.heading, border: `2px solid ${C.gold}`, borderRadius: 6, padding: '4px 8px', fontFamily: FONT, outline: 'none', width: 220 }}
            />
          ) : (
            <span
              onClick={() => { setLabelDraft(periodLabel); setEditingLabel(true); }}
              style={{ fontSize: 14, fontWeight: 700, color: C.heading, cursor: 'pointer', borderBottom: `2px dashed ${C.border}` }}
            >
              {periodLabel}
            </span>
          )}
        </div>

        <div>
          <div style={{ fontSize: 11, color: C.accent, marginBottom: 4 }}>PERIOD START</div>
          {/* min/max are a convenience so the picker greys out what the server
              would refuse. They are NOT the rule — POST /goals enforces both
              bounds and returns the message shown beneath. */}
          <input
            type="date"
            value={periodStart}
            min={PERIOD_MIN}
            max={todayISO()}
            onChange={e => setPeriodStart(e.target.value)}
            style={{ fontSize: 13, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px', fontFamily: FONT, background: C.bg, outline: 'none' }}
          />
        </div>

        <button
          onClick={savePeriod}
          disabled={savingPeriod}
          style={{
            marginLeft: 'auto',
            padding: '8px 18px',
            background: C.heading,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: FONT,
            opacity: savingPeriod ? 0.6 : 1,
          }}
        >
          {savingPeriod ? 'Saving…' : 'Apply Period'}
        </button>
      </div>
      )}

      {/* ── Summary banner ── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.heading}, #4A2800)`,
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#fff',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2, fontWeight: 600 }}>OVERALL PROGRESS</div>
          <div style={{ fontSize: 28, fontWeight: 800 }}>{overallPct}%</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
            {periodStart ? `Since ${new Date(periodStart + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : 'All time'}
          </div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            {scorable.filter(g => pct(g.actual, g.target_value) >= 100).length} / {scorable.length} targets hit
          </div>
        </div>
        {/* Mini bars — one per scorable metric, same set the headline averages */}
        <div style={{ width: '100%', display: 'flex', gap: 4 }}>
          {scorable.map(g => {
            const p = pct(g.actual, g.target_value);
            const sc = statusColor(p);
            return (
              <div key={g.metric_key} style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${p}%`, background: sc.bar, borderRadius: 99 }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Metric rows ── */}
      {metrics.map(goal => (
        <MetricRow
          key={goal.metric_key}
          goal={goal}
          onUpdate={handleUpdate}
          bottleneck={goal.metric_key === bottleneckKey}
          saveError={saveErrors[goal.metric_key]}
          onSaveError={setSaveError}
          windowDays={selWin.days}
        />
      ))}

      {/* ── Legend ── */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 12, color: C.accent, fontWeight: 600, marginBottom: 6 }}>HOW TO USE</div>
        <div style={{ fontSize: 12, color: C.accent, lineHeight: 1.7 }}>
          <strong style={{ color: C.text }}>Auto metrics</strong> — everything except 1:1 upsells updates live from your data, sales page landings included. Purchases, BMS and course figures count paying customers only. &nbsp;
          <strong style={{ color: C.text }}>Manual</strong> (1:1 upsells) — click the actual number to update it. &nbsp;
          <strong style={{ color: C.text }}>No target yet</strong> — click the dash beside a metric to set its first one. &nbsp;
          <strong style={{ color: C.text }}>Targets</strong> — click any target number to change it. &nbsp;
          <strong style={{ color: C.text }}>Period start</strong> — set a date to filter actuals from that date onwards.
        </div>
      </div>
    </div>
  );
};
